import * as React from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import LoadingSpinner from "../common/LoadingSpinner"
// Constants
import {
  AuthenticationConstants,
  OrganizationConstants,
  RoleConstants,
  DefaultApplicationConstants
} from "../common/utilities/appConstants";

// Configuration
import config from "../../config";
// Utilities
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "../common/utilities/applicationStorage";

// Styles
import './RouterWrapper.css'

// Context
import GlobalAuthenticatedContext from "../common/contexts/GlobalAuthenticatedContexts";

// Utilities
import { reportConsole } from "../common/utilities/logging-utilities";

import GenesysCloudAPI from "../common/static/GenesysCloudAPI";
import { fetchRolesAndPermissions } from '../../services/gc/createroleAndOAuthClientService'
import { fetchIntegrations, checkUsageTrialForTenant } from '../../services/api/integrationService'
// Static
const platformClient = window.require('platformClient');

const client = platformClient.ApiClient.instance;

const RouteWrapper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { globalAuthenticated, setGlobalAuthenticated } = React.useContext(GlobalAuthenticatedContext);
  const [genesysCloudAPIOptions] = React.useState(GenesysCloudAPI);

  const [isFetchingRoles, setIsFetchingRoles] = React.useState(true);
  const [integrationCheckCompleted, setIntegrationCheckCompleted] = React.useState(false);
  const [integrationFound, setIntegrationFound] = React.useState(false);

  // ----------- Step 1: Initial login and env setup -------------
  React.useEffect(() => {
    const initialize = async () => {
      let env: any = null;

      if (searchParams.has("gcHostOrigin")) {
        const url = searchParams.get("gcHostOrigin");
        if (!url) return navigate("/login");

        env = url.split("https://apps.")[1];
        setSessionStorageItem(OrganizationConstants.UsePopup, searchParams.get("usePopupAuth") || "");
        setSessionStorageItem(OrganizationConstants.Language, searchParams.get("langTag") || "");
      } else if (searchParams.has("environment")) {
        env = searchParams.get("environment");
        setSessionStorageItem(OrganizationConstants.UsePopup, searchParams.get("usePopupAuth") || "");
        setSessionStorageItem(OrganizationConstants.Language, searchParams.get("langTag") || "");
      } else {
        if (location.pathname.includes("setup")) return navigate("/setup");
        if (location.pathname.includes("summary")) return navigate("/summary");
        return navigate("/login");
      }

      if (!env) return navigate("/error", { state: { error: "Missing environment info" } });

      reportConsole("KBC --> Env Captured", "info", env);
      setSessionStorageItem(AuthenticationConstants.CloudCSPEnv, env);
      const apiEntry = genesysCloudAPIOptions.find((f) => f.env.includes(env));
      if (apiEntry) {
        setSessionStorageItem(OrganizationConstants.UrlAuth, apiEntry.auth);
        setSessionStorageItem(
          OrganizationConstants.UrlAuthShort,
          apiEntry.auth.split("https://login.")[1]
        );
        setSessionStorageItem(OrganizationConstants.UrlApi, apiEntry.api);
        setSessionStorageItem(OrganizationConstants.UrlApps, apiEntry.apps);
      }

      const redirectUri = `${window.location.protocol}//${window.location.host}/oauth/callback`;
      const gcEnv = getSessionStorageItem(AuthenticationConstants.CloudCSPEnv);
      const clientId = gcEnv === "inintca.com" ? config.inintca_dev_client : gcEnv === "inindca.com" ? config.inindca_dev_client : config.client;

      client.setEnvironment(gcEnv);
      await client.loginPKCEGrant(clientId, redirectUri);
    };

    if (
      globalAuthenticated &&
      !globalAuthenticated.contextLoaded &&
      !location.pathname.includes("login")
    ) {
      initialize();
    }
    // eslint-disable-next-line
  }, [genesysCloudAPIOptions, globalAuthenticated, location, navigate, searchParams]);


  // Role fetching
  React.useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { data } = await fetchRolesAndPermissions(config.client);
        let filteredRoles = data?.results?.grants
          .filter((grant: any) =>
            [RoleConstants.admin, RoleConstants.sharepointAdmin, RoleConstants.sharepointView]
              .includes(grant.role.name)
          )
          .map((item: any) => {
            const { default: _, selfUri, ...cleanedRole } = item.role;
            return cleanedRole;
          });

        setGlobalAuthenticated((prev: any) => ({
          ...prev,
          userRoles: [...filteredRoles],
        }));
      } catch (error: any) {
        if (error.status === 401) navigate("/unauthorized");
      } finally {
        setIsFetchingRoles(false);
      }
    };

    if (globalAuthenticated.contextLoaded) {
      fetchRoles();
    }
    // eslint-disable-next-line
  }, [globalAuthenticated.contextLoaded, navigate]);

  // Integration Check
  React.useEffect(() => {
    const checkIntegration = async () => {
      try {
        const response: any = await fetchIntegrations();
        const integration = response.data.results.find(
          (integration: any) =>
            integration.integrationType.id === DefaultApplicationConstants.DefaultKBCAppId
        );

        if (integration) {
          setIntegrationFound(true);
          setGlobalAuthenticated((prev: any) => ({
            ...prev,
            integrationFound: true
          }))
          navigate('/setup');
        } else {
          const trial = await checkUsageTrialForTenant();
          if (trial?.data?.results?.subscription_status === 'TRIAL') {
            setIntegrationFound(true);
            setGlobalAuthenticated((prev: any) => ({
              ...prev,
              usageLiscenseStatus: 'TRIAL',
              integrationFound: true
            }))
            navigate('/setup');

          }
        }
      } catch {
        setIntegrationFound(false);
        setGlobalAuthenticated((prev: any) => ({
          ...prev,
          integrationFound: false
        }))
        navigate('/unauthorized')
      } finally {
        setIntegrationCheckCompleted(true);
      }
    };

    if (globalAuthenticated.installed === false) {
      checkIntegration();
    } else {
      const checkTrial = async () => {
        // No need to check integration if already installed
        setIntegrationCheckCompleted(true);
        const trial = await checkUsageTrialForTenant();
        if (trial?.data?.results?.subscription_status === 'TRIAL') {
          setIntegrationFound(true);
          setGlobalAuthenticated((prev: any) => ({
            ...prev,
            usageLiscenseStatus: 'TRIAL',
          }));
        }
      };
      checkTrial();
    }
    // eslint-disable-next-line
  }, [globalAuthenticated.installed]);

  // Final Redirect Decision
  React.useEffect(() => {
    if (!isFetchingRoles && integrationCheckCompleted) {
      const allowedRoles = [RoleConstants.admin, RoleConstants.sharepointAdmin, RoleConstants.sharepointView];
      const userRoles = globalAuthenticated?.userRoles || [];

      const isAuthorized = userRoles.some((role: any) =>
        allowedRoles.includes(role.name)
      );

      if (globalAuthenticated.installed === false) {
        if (integrationFound) {
          if (location.pathname !== "/setup") navigate("/setup");
        } else {
          if (location.pathname !== "/unauthorized") navigate("/unauthorized");
        }
      } else {
        if (!isAuthorized && globalAuthenticated.installed === false && location.pathname !== "/unauthorized") {
          navigate("/unauthorized");
        }
      }
    }
    // eslint-disable-next-line
  }, [isFetchingRoles, integrationCheckCompleted, integrationFound, globalAuthenticated.installed, globalAuthenticated.userRoles, location.pathname, navigate]);

  if (isFetchingRoles || !integrationCheckCompleted) {
    return <LoadingSpinner title="Checking" message="Fetching your data..." />;
  }

  return (
    <div className="app-route-wrapper">
      <Outlet />
    </div>
  );
};


export default RouteWrapper;
